import React, { useState, useEffect } from 'react';

interface CreateBoxModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BoxFormData) => void;
  pieces: Array<{ id: string; code: string; nom: string }>;
  zones: Array<{ id: string; code: string; nom: string }>;
  editingBox?: any;
}

export interface BoxFormData {
  numero: string;
  nom: string;
  code: string;
  theme?: string;
  emplacementType: 'piece' | 'zone';
  pieceId?: string;
  zoneId?: string;
}

const THEMES = [
  'PEDAGOGIE',
  'JEUX_EXTERIEURS',
  'MATERIEL_CREATIF',
  'MUSIQUE_SON',
  'BUREAUTIQUE',
  'ACTIVITES_NATURE',
  'SECURITE',
  'DIVERS'
];

const CreateBoxModal: React.FC<CreateBoxModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  pieces,
  zones,
  editingBox
}) => {
  const [formData, setFormData] = useState<BoxFormData>({
    numero: '',
    nom: '',
    code: '',
    theme: '',
    emplacementType: 'piece',
    pieceId: '',
    zoneId: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (editingBox) {
      setFormData({
        numero: editingBox.numero || '',
        nom: editingBox.nom || '',
        code: editingBox.code || '',
        theme: editingBox.theme || '',
        emplacementType: editingBox.zoneId ? 'zone' : 'piece',
        pieceId: editingBox.pieceId || '',
        zoneId: editingBox.zoneId || '',
      });
    } else {
      setFormData({
        numero: '',
        nom: '',
        code: '',
        theme: '',
        emplacementType: 'piece',
        pieceId: '',
        zoneId: '',
      });
    }
  }, [editingBox, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nom.trim()) newErrors.nom = 'Le nom est obligatoire';
    if (!formData.code.trim()) newErrors.code = 'Le code est obligatoire';
    
    if (formData.emplacementType === 'piece' && !formData.pieceId) {
      newErrors.pieceId = 'La pièce est obligatoire';
    }
    if (formData.emplacementType === 'zone' && !formData.zoneId) {
      newErrors.zoneId = 'La zone est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Nettoyer les données selon le type d'emplacement
      const submitData = {
        ...formData,
        pieceId: formData.emplacementType === 'piece' ? formData.pieceId : undefined,
        zoneId: formData.emplacementType === 'zone' ? formData.zoneId : undefined,
      };
      onSubmit(submitData);
      setFormData({ numero: '', nom: '', code: '', theme: '', emplacementType: 'piece', pieceId: '', zoneId: '' });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  📦 {editingBox ? 'Modifier la Box Mobile' : 'Nouvelle Box Mobile'}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Numéro */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro
                  </label>
                  <input
                    type="text"
                    name="numero"
                    value={formData.numero}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 01, 02, A1"
                  />
                </div>

                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: BOX-01, BOX-A"
                    maxLength={50}
                  />
                  {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                </div>

                {/* Nom */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.nom ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Box Mobile Jeux, Box Pédagogie"
                  />
                  {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                </div>

                {/* Thème */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thème
                  </label>
                  <select
                    name="theme"
                    value={formData.theme}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Aucun (attribution automatique)</option>
                    {THEMES.map(theme => (
                      <option key={theme} value={theme}>{theme.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Laissez vide pour attribution automatique selon le contenu
                  </p>
                </div>

                {/* Type d'emplacement */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emplacement <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4 mb-3">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="emplacementType"
                        value="piece"
                        checked={formData.emplacementType === 'piece'}
                        onChange={handleChange}
                        className="form-radio text-blue-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Dans une pièce</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="emplacementType"
                        value="zone"
                        checked={formData.emplacementType === 'zone'}
                        onChange={handleChange}
                        className="form-radio text-blue-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Dans une zone</span>
                    </label>
                  </div>

                  {formData.emplacementType === 'piece' && (
                    <select
                      name="pieceId"
                      value={formData.pieceId}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.pieceId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionner une pièce</option>
                      {pieces.map(piece => (
                        <option key={piece.id} value={piece.id}>
                          {piece.nom} ({piece.code})
                        </option>
                      ))}
                    </select>
                  )}

                  {formData.emplacementType === 'zone' && (
                    <select
                      name="zoneId"
                      value={formData.zoneId}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.zoneId ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionner une zone</option>
                      {zones.map(zone => (
                        <option key={zone.id} value={zone.id}>
                          {zone.nom} ({zone.code})
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {errors.pieceId && <p className="mt-1 text-sm text-red-600">{errors.pieceId}</p>}
                  {errors.zoneId && <p className="mt-1 text-sm text-red-600">{errors.zoneId}</p>}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {editingBox ? 'Modifier la box' : 'Créer la box'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateBoxModal;

